import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, Loader2, Pencil } from "lucide-react";
import { WritingEditor } from "@/components/WritingEditor";
import { writingPathWrite } from "@/lib/api";

// WRITE THE ESSAY (Increment 4) — writing is the foreground. One authoritative draft (the existing OT units); this shell only orchestrates:
// where am I · what this part needs to do · the writing surface · Ask TWS (shell-level) · a compact essay map.
const AUTOSAVE_MS = 1200;
const btn = "inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] disabled:opacity-40 transition-colors";
const ghost = "inline-flex items-center gap-2 border border-stone-400 text-stone-800 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-stone-100 disabled:opacity-40 transition-colors";
const link = "text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-4 disabled:opacity-40";

// THE ESSAY MAP — the one compact orientation the student needs while writing and revising: where the thesis lives, what each part is for,
// what is developed, which part is open. No diagnostics or internal states here; Big Picture is not needed for ordinary orientation.
export function EssayMap({ parts, currentId, flaggedId, onOpen, busy, thesis = "", onReviseThesis = null, nextId = "" }) {
  const mark = (p) => (p.id === currentId ? <span className="text-[#8C3A2A]"> → You are here</span> : p.id === nextId ? <span className="text-stone-500"> · Next</span> : null);
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(thesis);
  const Row = ({ p, children }) => (
    <button data-testid={`write-map-${p.id}`} onClick={() => onOpen(p.id)} disabled={busy}
      className={`w-full text-left px-2 py-1.5 rounded-sm transition-colors ${p.id === currentId ? "bg-[#8C3A2A]/10 ring-1 ring-[#8C3A2A]/40" : "hover:bg-stone-100"}`}>
      {children}
    </button>
  );
  const body = parts.filter((p) => p.region === "body");
  return (
    <nav data-testid="write-map" className="bg-white/80 border border-stone-200 rounded-md p-3">
      <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">Essay map</p>
      <ol className="space-y-1">
        {parts.map((p) => (
          <li key={p.id}>
            {p.region === "body" ? (
              <Row p={p}>
                <span className={`block text-[13px] ${p.id === currentId ? "text-stone-900" : "text-stone-700"}`}>Part {body.indexOf(p) + 1}{p.id === flaggedId ? " ·" : ""}{mark(p)}</span>
                <span className="block text-[12px] text-stone-500 leading-snug">Purpose: {p.purpose || "none yet"}</span>
                {p.needs ? <span className="block text-[11px] text-stone-500 leading-snug mt-0.5">Still needs developing: {p.needs}</span> : <span className="block text-[10px] font-mono-panel text-stone-400">✓ developed for now</span>}
              </Row>
            ) : p.region === "introduction" ? (
              <div className={`rounded-sm ${p.id === currentId ? "bg-[#8C3A2A]/10 ring-1 ring-[#8C3A2A]/40" : ""}`}>
                <Row p={p}><span className="block text-[13px] text-stone-800">Introduction{mark(p)}</span><span className="block text-[11px] text-stone-400">Context / setup, then the thesis</span></Row>
                <div className="px-2 pb-2">
                  <p data-testid="write-thesis" className="text-[12px] leading-snug"><span className="text-[#8C3A2A]">Thesis / organizing idea: </span>
                    {editing ? null : <span data-testid="write-thesis-text" className="text-stone-800">{thesis}</span>}</p>
                  {editing ? (
                    <div className="mt-1 space-y-1">
                      <textarea data-testid="write-thesis-input" value={t} onChange={(e) => setT(e.target.value)} rows={3} className="w-full border border-stone-300 rounded-sm px-2 py-1 text-[12px]" />
                      <div className="flex gap-3">
                        <button data-testid="write-thesis-save" onClick={async () => { await onReviseThesis(t.trim()); setEditing(false); }} disabled={busy || !t.trim()} className={link}>Save</button>
                        <button data-testid="write-thesis-cancel" onClick={() => setEditing(false)} className={link}>Cancel</button>
                      </div>
                    </div>
                  ) : onReviseThesis ? <button data-testid="write-thesis-revise" onClick={() => { setT(thesis); setEditing(true); }} disabled={busy} className={link}>Revise thesis</button> : null}
                  {p.needs ? <p className="text-[11px] text-stone-500 leading-snug mt-1">Still needs developing: {p.needs}</p> : <p className="text-[10px] font-mono-panel text-stone-400 mt-1">✓ context developed for now</p>}
                </div>
              </div>
            ) : (
              <Row p={p}>
                <span className="block text-[13px] text-stone-800">Conclusion{mark(p)}</span>
                {p.needs ? <span className="block text-[11px] text-stone-500 leading-snug mt-0.5">{p.word_count ? "Still needs developing" : "What it will eventually need to do"}: {p.needs}</span> : <span className="block text-[10px] font-mono-panel text-stone-400">✓ developed for now</span>}
              </Row>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// (kept for callers that still want a thesis drawer; the Essay map now carries the thesis)
export function ThesisDrawer({ thesis, busy, onRevise, readOnly }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(thesis);
  return (
    <div data-testid="write-thesis" className="bg-white/80 border border-stone-200 rounded-md p-3">
      <button data-testid="write-thesis-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between font-mono-panel text-[10px] uppercase tracking-[0.22em] text-stone-500">
        Working thesis <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !editing && (
        <div className="mt-2">
          <p data-testid="write-thesis-text" className="text-[14px] text-stone-900 leading-snug">{thesis}</p>
          {!readOnly && <button data-testid="write-thesis-revise" onClick={() => { setT(thesis); setEditing(true); }} disabled={busy} className={`${link} mt-2`}>Revise thesis</button>}
        </div>
      )}
      {open && editing && (
        <div className="mt-2 space-y-2">
          <textarea data-testid="write-thesis-input" value={t} onChange={(e) => setT(e.target.value)} rows={3} className="w-full border border-stone-300 rounded-sm px-2 py-1.5 text-[14px]" />
          <div className="flex gap-2">
            <button data-testid="write-thesis-save" onClick={async () => { await onRevise(t.trim()); setEditing(false); }} disabled={busy || !t.trim()} className={btn}>Save</button>
            <button data-testid="write-thesis-cancel" onClick={() => setEditing(false)} className={ghost}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// CURRENT STRUCTURAL FOCUS — the close-up. Where you are · what this part is doing · what still needs developing · next. Same words as the map and the whole view.
export const TAG = "font-mono-panel text-[10px] uppercase tracking-[0.2em]";
export function ThesisStrip({ thesis, current }) {
  return (
    <div data-testid="write-thesis-strip" className="mb-4 border-l-2 border-stone-300 pl-4">
      <p className={`${TAG} text-stone-500`}>Introduction <span className="text-stone-300">›</span> <span className="text-[#8C3A2A]">Thesis / organizing idea</span> <span className="normal-case tracking-normal text-stone-400">— the main claim your essay is developing</span></p>
      <p data-testid="write-strip-thesis" className="font-serif-display text-[17px] text-stone-900 leading-snug mt-1">{thesis}</p>
      {current?.region === "body" && <p className="text-[12px] text-stone-500 mt-1">↓ Your argument now has to establish why this claim works.</p>}
    </div>
  );
}

export function PartHeader({ part, thesis, guides, busy, act, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(part.purpose);
  const body = part.region === "body";
  return (
    <header data-testid="write-part-header" className="mb-4 border-l-2 border-[#8C3A2A] pl-4 bg-[#8C3A2A]/[0.04] py-3 pr-3 rounded-r-sm">
      <p className={`${TAG} text-[#8C3A2A]`}>{part.label} <span data-testid="write-you-are-here" className="ml-2 text-stone-900">→ You are here</span></p>
      {part.region === "introduction" && (
        <div className="mt-2">
          <p className={`${TAG} text-stone-400`}>Context / setup, then the thesis</p>
          <p data-testid="write-intro-thesis" className="text-[14px] text-stone-700 mt-1"><span className="text-[#8C3A2A]">Thesis / organizing idea:</span> {thesis}</p>
          <p className={`${TAG} text-stone-400 mt-3`}>Purpose</p>
          <p data-testid="write-part-purpose" className="text-[15px] text-stone-900 leading-relaxed">{guides.intro}</p>
        </div>
      )}
      {part.region === "conclusion" && (
        <div className="mt-2">
          <p className={`${TAG} text-stone-400`}>Purpose</p>
          <p data-testid="write-part-purpose" className="text-[15px] text-stone-900 leading-relaxed">{guides.conclusion}</p>
        </div>
      )}
      {body && (
        <div className="mt-2">
          <p className={`${TAG} text-stone-400`}>Purpose</p>
          {editing ? (
            <div className="mt-1 flex gap-2">
              <input data-testid="write-purpose-input" value={t} onChange={(e) => setT(e.target.value)} className="flex-1 border border-stone-300 rounded-sm px-2 py-1 text-[14px]" />
              <button data-testid="write-purpose-save" onClick={async () => { await act({ action: "set_purpose", part_id: part.id, text: t }); setEditing(false); }} disabled={busy || !t.trim()} className={btn}>Save</button>
            </div>
          ) : (
            <p data-testid="write-part-purpose" className="text-[15px] text-stone-900 leading-relaxed">
              {part.purpose || "This part has no stated job yet."}
              {!readOnly && <button data-testid="write-purpose-edit" onClick={() => { setT(part.purpose); setEditing(true); }} disabled={busy} className={`${link} ml-3`}><Pencil className="inline h-3 w-3 mr-1" />Revise this purpose</button>}
            </p>
          )}
        </div>
      )}
      {part.needs ? (
        <div className="mt-3">
          <p className={`${TAG} text-stone-400`}>What still needs developing</p>
          <p data-testid="write-part-needs" className="text-[14px] text-stone-800 leading-relaxed">{part.needs}</p>
        </div>
      ) : part.word_count > 0 ? <p data-testid="write-part-needs" className={`${TAG} text-stone-400 mt-3`}>✓ developed for now</p> : null}
    </header>
  );
}

export function NextStrip({ part, onOpen, busy }) {
  if (!part) return <p data-testid="write-next-strip" className={`${TAG} text-stone-400 mt-4`}>Next — nothing still needs developing; check the whole essay when you are ready.</p>;
  return (
    <div data-testid="write-next-strip" className="mt-4 border-l-2 border-dashed border-stone-300 pl-4">
      <p className={`${TAG} text-stone-500`}>Next <span className="normal-case tracking-normal text-stone-400">— recommended, not required</span></p>
      <p className="text-[14px] text-stone-800 mt-0.5">{part.label}{part.region === "body" && part.purpose ? ` — ${part.purpose}` : ""}</p>
      {part.needs && <p className="text-[12px] text-stone-500 leading-snug">{part.region === "body" && part.purpose ? "" : part.needs}</p>}
      <button data-testid="write-next-open" onClick={() => onOpen(part.id)} disabled={busy} className={`${link} mt-1`}>Go there</button>
    </div>
  );
}

export default function WriteEssayWorkspace({ sessionId, thesis, write, phase, onUpdate, onDraftChange, onWholeCheck }) {
  // (strengthen_finish is rendered by StrengthenFinishWorkspace; this surface is drafting only)
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [checkedWhole, setCheckedWhole] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);
  const [viewId, setViewId] = useState("");
  const [switching, setSwitching] = useState(false);   // while a part change is in flight, keystrokes must not land in the old part            // read-only browsing after the draft is complete (no server round-trip)
  const parts = write?.parts || [];
  const readOnly = phase === "strengthen_finish";
  const current = (readOnly && parts.find((p) => p.id === viewId)) || parts.find((p) => p.id === write?.current_part_id) || parts[0];
  const idx = parts.findIndex((p) => p.id === current?.id);
  const next = parts.find((p) => p.id === write?.next_part_id) || null;   // recommended next structural job (guidance, not a lock)

  const act = async (payload) => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      if (payload.action === "complete_check") setCheckedWhole(true);
      onUpdate(await writingPathWrite(sessionId, payload));
      if (payload.action === "complete_check") onWholeCheck?.();   // TWS steps the student back to the whole essay, with its reason
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const flush = async () => {
    if (!pending.current) return;
    const { part_id, text, formatting } = pending.current;
    pending.current = null;
    setSaving(true);
    try { onUpdate(await writingPathWrite(sessionId, { action: "save", part_id, text, formatting })); }
    catch (_) { /* the editor keeps the text; next keystroke retries */ }
    finally { setSaving(false); }
  };
  const onChange = (partId) => ({ text, formatting }) => {
    onDraftChange?.(text);
    pending.current = { part_id: partId, text, formatting };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, AUTOSAVE_MS);
  };
  useEffect(() => () => { clearTimeout(timer.current); }, []);
  const open = async (partId) => {
    if (readOnly) { setViewId(partId); return; }
    setSwitching(true);
    try { clearTimeout(timer.current); await flush(); await act({ action: "open_part", part_id: partId }); onDraftChange?.(""); }
    finally { setSwitching(false); }
  };

  if (!current) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
      <aside className="space-y-3 md:sticky md:top-6 self-start">
        <EssayMap parts={parts} currentId={current.id} flaggedId={write?.coach_part_id} onOpen={open} busy={busy} thesis={thesis} nextId={write?.next_part_id}
          onReviseThesis={readOnly ? null : (t) => act({ action: "revise_thesis", text: t })} />
        {!readOnly && <button data-testid="write-back-to-plan" onClick={async () => { await flush(); await act({ action: "back_to_plan" }); }} disabled={busy} className={link}>Return to the plan</button>}
      </aside>

      <section data-testid="write-essay" className="bg-white border border-stone-200 rounded-md p-6 min-w-0">
        {current.region !== "introduction" && <ThesisStrip thesis={thesis} current={current} />}
        <PartHeader part={current} thesis={thesis} guides={{ intro: write.intro_guide, conclusion: write.conclusion_guide }} busy={busy} act={act} readOnly={readOnly} />
        {readOnly ? (
          <div data-testid="write-readonly" className="text-[16px] leading-[1.8] text-stone-900 whitespace-pre-wrap min-h-[160px] border border-stone-200 rounded-md p-4 bg-stone-50/50">{current.text || <span className="text-stone-400 italic">Nothing written here.</span>}</div>
        ) : (
          <div className={switching ? "pointer-events-none opacity-50" : ""}><WritingEditor key={current.id} testid="write-editor" initialText={current.text} initialFormatting={current.formatting} onChange={onChange(current.id)} onBlur={flush}
            autoFocus={false} minHeightPx={260} placeholder={current.region === "introduction" ? "Begin your introduction…" : current.region === "conclusion" ? "Begin your conclusion…" : "Write this part of your essay…"} /></div>
        )}
        <div className="mt-2 flex items-center justify-between text-[11px] text-stone-400 font-mono-panel">
          <span data-testid="write-word-count">{current.word_count} words</span>
          <span data-testid="write-save-state">{saving ? "Saving…" : pending.current ? "Unsaved changes" : "Saved"}</span>
        </div>
        {err && <p data-testid="write-error" className="mt-3 text-[13px] text-[#8C3A2A]">{err}</p>}
        <NextStrip part={next} onOpen={open} busy={busy} />

        {write?.coach_message && (
          <div data-testid="write-coach" className="mt-5 text-[14px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-4 leading-relaxed">
            <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-1">TWS</p>
            {write.coach_message}
            {write.coach_issue === "drift" && <p className="mt-2 text-[12px] text-stone-500">You can revise this part's purpose above, keep writing, or return to the plan.</p>}
          </div>
        )}

        {!readOnly && (
          <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
            <button data-testid="write-check-part" onClick={async () => { await flush(); await act({ action: "check_part", part_id: current.id }); }} disabled={busy} className={ghost}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Check this part
            </button>
            {next && <button data-testid="write-next-part" onClick={() => open(next.id)} disabled={busy} className={btn}>Next: {next.label} <ArrowRight className="h-4 w-4" /></button>}
            <span className="flex-1" />
            <button data-testid="write-complete" onClick={async () => { await flush(); await act({ action: "complete_check" }); }} disabled={busy} className={link}>I have a complete draft</button>
            {checkedWhole && write?.coach_issue === "gap" && parts.every((p) => p.word_count > 0) && (
              <button data-testid="write-complete-anyway" onClick={() => act({ action: "complete_anyway" })} disabled={busy} className={link}>Go on anyway</button>
            )}
          </div>
        )}
        {readOnly && write?.finish_message && (
          <div data-testid="write-finish-message" className="mt-6 text-[15px] text-stone-900 bg-[#f3efe6] border border-[#d9cfb8] rounded-sm p-4 leading-relaxed">
            {write.finish_message}
            <p className="mt-2 text-[12px] text-stone-500">Strengthening the essay is the next step in The Writing Studio.</p>
          </div>
        )}
      </section>
    </div>
  );
}
